import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, AlertTriangle, Bot, Database, ShoppingBag, Shield, MessageSquare, Scale } from 'lucide-react';
import { IMAGES, bgStyle } from '@/config/images';

const Section = ({ icon: Icon, iconColor = 'text-orange-500', title, children }) => (
  <div className="bg-black/70 backdrop-blur-sm border border-zinc-800 rounded-lg p-8">
    <div className="flex items-center gap-3 mb-6">
      <Icon className={`w-6 h-6 ${iconColor}`} />
      <h2 className="text-xl font-bold text-white">{title}</h2>
    </div>
    <div className="prose prose-invert max-w-none text-zinc-300 space-y-3">
      {children}
    </div>
  </div>
);

const TermsPage = () => {
  const lastUpdated = 'April 2026';

  return (
    <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" style={bgStyle(IMAGES.TERMS_PRIVACY)}>
      <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
      <div className="max-w-4xl mx-auto">

        {/* Back Link */}
        <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-orange-500/20 rounded-lg">
              <FileText className="w-8 h-8 text-orange-500" />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-orange-500 mb-1">LEGAL</div>
              <h1
                className="text-3xl md:text-4xl font-bold text-orange-500 tracking-tight uppercase"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Terms of Service
              </h1>
              <p className="text-sm text-zinc-500 mt-1">
                Last updated: {lastUpdated} &nbsp;·&nbsp; CNC-Helpdesk (Andre Booysen)
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6">

          {/* 1. Beta Disclaimer */}
          <Section icon={AlertTriangle} iconColor="text-yellow-400" title="1. Beta Product Disclaimer">
            <p>
              CNC-Helpdesk is currently in <strong className="text-white">beta</strong>. Features, pricing,
              and behaviour may change at any time without prior notice as the platform evolves.
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>No uptime guarantee is made during the beta period. Downtime, maintenance windows, and interruptions may occur.</li>
              <li>Data structures and stored settings may be reset or migrated during significant updates.</li>
              <li>New features may be added or removed based on user feedback and platform direction.</li>
            </ul>
            <p>
              By using CNC-Helpdesk during beta, you acknowledge these conditions and agree not to rely on the service for
              time-critical or production-critical operations.
            </p>
          </Section>

          {/* 2. AI-Generated Content */}
          <Section icon={Bot} iconColor="text-blue-400" title="2. AI-Generated Content Disclaimer">
            <p>
              Certain features of CNC-Helpdesk — including the Ask AI assistant, troubleshooting guidance,
              and calculator suggestions — are powered by <strong className="text-white">Claude Sonnet</strong> (Anthropic).
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>AI responses are for <strong className="text-white">guidance and informational purposes only</strong>. They do not constitute professional engineering, manufacturing, or safety advice.</li>
              <li>All AI-generated recommendations should be verified against your machine manufacturer's documentation before use.</li>
              <li>CNC machining and laser cutting involve potentially dangerous equipment. <strong className="text-white">You are solely responsible for safe machine operation.</strong></li>
              <li>AI responses may occasionally contain errors, outdated information, or context mismatches. Always apply your own judgement.</li>
              <li>CNC-Helpdesk and its developer accept no liability for damage, injury, or loss resulting from acting on AI-generated content.</li>
            </ul>
          </Section>

          {/* 3. Data Storage */}
          <Section icon={Database} iconColor="text-cyan-400" title="3. Data Storage & Hosting">
            <p>
              User account data, machine profiles, community posts, marketplace listings, and app settings are stored in
              <strong className="text-white"> MongoDB Atlas</strong>, a cloud-hosted database service.
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Where available, data is hosted in the <strong className="text-white">South Africa (Johannesburg)</strong> region to minimise latency for local users.</li>
              <li>MongoDB Atlas applies encryption at rest and in transit to all stored data.</li>
              <li>CNC-Helpdesk does not store payment card details — payments are processed by Stripe, which maintains its own security certifications.</li>
              <li>Data is not sold, shared with third parties for marketing, or used to train AI models.</li>
            </ul>
            <p>
              For full details on what is collected and how it is used, see our{' '}
              <Link to="/privacy" className="text-orange-400 hover:underline">Privacy Policy</Link>.
            </p>
          </Section>

          {/* 4. Marketplace & User-Generated Content */}
          <Section icon={ShoppingBag} iconColor="text-green-400" title="4. Marketplace & User-Generated Content">
            <p>
              CNC-Helpdesk provides a marketplace and community features where users can post listings,
              share settings, and interact with other members.
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong className="text-white">Users are fully responsible</strong> for the accuracy and legality of their own marketplace listings.</li>
              <li>CNC-Helpdesk acts as a platform only. Transactions, disputes, and fulfilment are solely between buyers and sellers.</li>
              <li>You may not post content that is illegal, offensive, misleading, or that infringes on the intellectual property of others.</li>
              <li>Community-shared settings are provided by other users and have not been verified by CNC-Helpdesk. Use them at your own risk.</li>
              <li>CNC-Helpdesk reserves the right to remove any listing or post that violates these terms.</li>
            </ul>
          </Section>

          {/* 5. No Warranties */}
          <Section icon={Shield} iconColor="text-red-400" title="5. No Warranties — Provided As-Is">
            <p>
              The Service is provided <strong className="text-white">"as is"</strong> and{' '}
              <strong className="text-white">"as available"</strong> during the beta period, without warranty of any kind,
              express or implied.
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>CNC-Helpdesk does not warrant that the service will be error-free, uninterrupted, or meet any specific performance standard.</li>
              <li>Calculations, settings, and recommendations are starting points only. Your specific machine, material, and environment will always require independent verification.</li>
              <li>To the maximum extent permitted by applicable law, CNC-Helpdesk and its developer are not liable for any direct, indirect, incidental, or consequential damages arising from use of the service.</li>
            </ul>
          </Section>

          {/* 6. Contact */}
          <Section icon={MessageSquare} iconColor="text-orange-400" title="6. Contact & Feedback">
            <p>
              We do not operate a support email inbox as our primary contact channel during beta.
              If you have questions, issues, or feedback, please use the{' '}
              <strong className="text-white">Feedback button</strong> available inside the app (visible in the lower corner
              when logged in).
            </p>
            <p>
              For account-related or legal enquiries, you may also reach us at{' '}
              <a href="mailto:andre@cnc-helpdesk.com" className="text-orange-400 hover:underline">
                andre@cnc-helpdesk.com
              </a>.
            </p>
          </Section>

          {/* 7. Governing Law */}
          <Section icon={Scale} iconColor="text-purple-400" title="7. Governing Law">
            <p>
              These Terms of Service are governed by and construed in accordance with the laws of the{' '}
              <strong className="text-white">Republic of South Africa</strong>.
            </p>
            <p>
              Any disputes arising from the use of CNC-Helpdesk shall be subject to the jurisdiction of the
              South African courts.
            </p>
            <p>
              The Protection of Personal Information Act (POPIA) applies to the collection and processing of
              personal information of South African users.
            </p>
          </Section>

          {/* 8. General */}
          <div className="bg-black/70 backdrop-blur-sm border border-zinc-800 rounded-lg p-8">
            <h2 className="text-xl font-bold text-white mb-4">8. General</h2>
            <div className="text-zinc-300 space-y-3">
              <p>By accessing or using CNC-Helpdesk you agree to be bound by these Terms of Service. If you do not agree, you may not use the Service.</p>
              <p>These terms may be updated at any time. Continued use after an update constitutes acceptance of the revised terms. The "Last updated" date at the top of this page will reflect any changes.</p>
              <p>
                For 18 years of age requirement, account responsibilities, subscription billing, and intellectual property details,
                refer to the full combined policy at{' '}
                <Link to="/terms" className="text-orange-400 hover:underline">/terms</Link>.
              </p>
            </div>
          </div>

          {/* Cross-link to Privacy Policy */}
          <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-white font-semibold mb-1">Privacy Policy</p>
              <p className="text-zinc-400 text-sm">How your data is collected, stored, and protected.</p>
            </div>
            <Link
              to="/privacy"
              className="px-4 py-2 bg-orange-500/20 border border-orange-500/40 text-orange-400 hover:bg-orange-500/30 rounded text-sm font-medium transition-colors whitespace-nowrap"
            >
              View Privacy Policy →
            </Link>
          </div>

        </div>

        <div className="h-12" />
      </div>
    </div>
  );
};

export default TermsPage;
